import {
  WSButton,
  WSCard,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSIcon,
  WSLabel,
  WSList,
  WSLoader,
  WSPill,
  WSPillProps,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ExternalBankAccount,
  ExternalBankAccountStatus,
  ListExternalBankAccountsFilterParameterVerificationMethodEq
} from "@wingspanhq/wingspan-api-sdk";
import { queriesExternalBankAccounts } from "@query";
import React from "react";
import { routeHelpersAccountStakeholdersAdd } from "../../AccountStakeholders/paths";
import { avatarBankAccount } from "../../../shared/constants/avatars";
import { useExternalBankAccountReconnect } from "../../../shared/hooks/useExternalBankAccountReconnect";
import { useExternalBankAccountsPlaidLink } from "../../../shared/hooks/useExternalBankAccountsPlaidLink";
import { selectorExternalBankAccountInstitution } from "../../../v3/selectors/selectorExternalBankAccountInstitution";
import { selectorExternalBankAccountName } from "../../../v3/selectors/selectorExternalBankAccountName";
import { openInNewTab } from "../../../utils/openInNewTab";
import { useModalAddTransaction } from "../modals/ModalAddTransaction";

const KNOWLEDGE_CENTER_URL =
  "https://my.wingspan.app/member/knowledge-center/freelance-taxes-101";
const HELP_CENTER_URL =
  "https://help.wingspan.app/en/articles/4552381-how-does-wingspan-bookkeeping-work";

type PromoCardProps = {
  emoji: string;
  title: string;
  description: string;
  onClick: () => void;
};

const PromoCard: React.FC<PromoCardProps> = props => (
  <WSCard onClick={props.onClick}>
    <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
      <WSFlexBox.CenterY wrap="nowrap">
        <WSText.Heading2 mr="M">{props.emoji}</WSText.Heading2>
        <WSElement>
          <WSText.Heading5>{props.title}</WSText.Heading5>
          <WSText.ParagraphSm mt="XS" color="gray600">
            {props.description}
          </WSText.ParagraphSm>
        </WSElement>
      </WSFlexBox.CenterY>
      <WSIcon block name="chevron-right" size="S" color="gray500" ml="M" />
    </WSFlexBox.CenterY>
  </WSCard>
);

export const CardAddTransactions: React.FC = () => {
  const modalAddTransaction = useModalAddTransaction();
  return (
    <PromoCard
      emoji="🤑"
      title="Add transactions to find write-offs"
      description="Independent professionals can save an average of $3,271.00. We'll help make finding savings easy."
      onClick={() => {
        void modalAddTransaction.open({});
      }}
    />
  );
};

export const CardShareWithAccountant: React.FC = () => {
  const goToAddTeamMember = routeHelpersAccountStakeholdersAdd.useGoTo();
  return (
    <PromoCard
      emoji="🤝"
      title="Share access with your accountant"
      description="Invite your accountant or team to help manage your finances."
      onClick={() => goToAddTeamMember()}
    />
  );
};

export const CardTrackPayments: React.FC = () => (
  <PromoCard
    emoji="💰"
    title="Track your payments through Wingspan"
    description="Automate your payment workflow and get reports on income by client."
    onClick={() => openInNewTab("/member/invoices")}
  />
);

export const CardTaxes101: React.FC = () => (
  <WSCard
    header={{
      label: "Freelance Taxes 101 with Little Fish Accounting",
      value: { pill: { theme: "neutral", icon: "caret-right" } }
    }}
    onClick={() => openInNewTab(KNOWLEDGE_CENTER_URL)}
  >
    <WSText.ParagraphSm color="gray600">
      Learn the common deductible expenses for taxes. Little Fish Accounting's
      checklist to help you navigate freelance tax deductions.
    </WSText.ParagraphSm>
  </WSCard>
);

export const HelpCenterLink: React.FC = () => (
  <WSText.ParagraphSm align="center" color="gray600">
    Need help? Visit our{" "}
    <WSButton.Link onClick={() => openInNewTab(HELP_CENTER_URL)}>
      bookkeeping help center
    </WSButton.Link>
    .
  </WSText.ParagraphSm>
);

const statusPillProps = (
  status: ExternalBankAccountStatus
): WSPillProps | null => {
  switch (status) {
    case ExternalBankAccountStatus.VERIFIED:
      return { theme: "success", icon: true, text: "Connected" };
    case ExternalBankAccountStatus.PENDING:
      return { theme: "warning", icon: true, text: "Pending" };
    case ExternalBankAccountStatus.FAILED:
      return { theme: "error", icon: true, text: "Failed" };
    default:
      return null;
  }
};

const AccountRow: React.FC<{ account: ExternalBankAccount }> = props => {
  const reconnect = useExternalBankAccountReconnect();
  const isDisconnected =
    props.account.status === ExternalBankAccountStatus.DISCONNECTED;
  const pill = statusPillProps(props.account.status);

  return (
    <WSFlexBox.CenterY justify="space-between" wrap="nowrap">
      <WSLabel
        text={selectorExternalBankAccountInstitution(props.account)}
        secondaryText={selectorExternalBankAccountName(props.account)}
        avatar={avatarBankAccount}
      />
      {isDisconnected ? (
        <WSButton.Link onClick={() => void reconnect(props.account)}>
          Reconnect
        </WSButton.Link>
      ) : pill ? (
        <WSPill {...pill} />
      ) : null}
    </WSFlexBox.CenterY>
  );
};

export const CardYourAccounts: React.FC = () => {
  // v3 has no bookkeeping-usage flag (WIN-15217), so scope to Plaid-linked
  // accounts as the closest approximation of the v1 bookkeeping-synced set.
  // The verification-method scoping is now applied server-side (WIN-15218);
  // REMOVED accounts still need a client-side drop (no status list filter).
  const queryAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery({
      filter: {
        verificationMethod: {
          eq: ListExternalBankAccountsFilterParameterVerificationMethodEq.PLAID
        }
      }
    });
  const plaidLink = useExternalBankAccountsPlaidLink({
    onSuccess: () => undefined
  });

  const accounts = (queryAccounts.data?.data ?? []).filter(
    account => account.status !== ExternalBankAccountStatus.REMOVED
  );

  return (
    <WSCard header={{ label: "Your Accounts" }}>
      {queryAccounts.isLoading ? (
        <WSElement my="XL">
          <WSLoader.Spinner />
        </WSElement>
      ) : accounts.length ? (
        <WSList gap="M">
          {accounts.map(account => (
            <AccountRow key={account.id} account={account} />
          ))}
        </WSList>
      ) : (
        <WSEmptyState
          size="S"
          type="documents"
          title="No accounts connected yet"
          description="Connect a bank account to automatically sync transactions."
        />
      )}
      <WSButton.Link
        icon="plus-circle"
        mt="XL"
        loading={plaidLink.meta.isLoading}
        onClick={() => plaidLink.open()}
        data-testid="bookkeepingAddAccount"
      >
        Add account
      </WSButton.Link>
    </WSCard>
  );
};
